<template>
  <BittsLayout :is-page="true">
    <template #content>
      <MobileGuard>
        <section class="individual-record-page-v2">
          <IRPHeaderV2
            :source-id="sourceId"
            :source-primary-key="sourcePrimaryKey"
            class="mb-24"
            @list-modal-visible="isListModalVisible = true"
          />
          <IRPTabBar
            :current-tab="currentTab"
            @change:tab="onTabChange"
            class="mb-24"
          />
          <div role="tabpanel" :aria-label="activeTabName">
            <RouterViewWrapper
              :key="individualRecordStore.recordKey"
              :source-id="sourceId"
              :source-primary-key="sourcePrimaryKey"
            />
          </div>
        </section>
        <ListModal
          analytics-location="IRP"
          :selected-records-to-add="currentRecordForList"
          :available-partner-orgs-for-list="partnerOrgIds"
          :visible="isListModalVisible"
          @closed="isListModalVisible = false"
        />
      </MobileGuard>
    </template>
  </BittsLayout>
</template>

<script setup lang="ts">
import { BittsLayout } from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import MobileGuard from '@/components/MobileGuard.vue';
import {
  type IRPTabType,
  IRP_TAB,
  IRP_TAB_ROUTES,
  irpTabName,
} from '@/components/records/v2/irp';
import IRPHeaderV2 from '@/components/records/v2/IRPHeaderV2.vue';
import IRPTabBar from '@/components/records/v2/IRPTabBar.vue';
import ListModal from '@/components/shared-list/ListModal.vue';

import {
  useIndividualRecordStore,
  useMatchIssuesStore,
  usePartnersStore,
  usePopulationsStore,
} from '@/stores';

type Props = {
  sourceId: number;
  sourcePrimaryKey: string;
};

const { sourceId, sourcePrimaryKey } = defineProps<Props>();

const route = useRoute();
const router = useRouter();

const individualRecordStore = useIndividualRecordStore();
const partnersStore = usePartnersStore();
const populationsStore = usePopulationsStore();
const matchIssuesStore = useMatchIssuesStore();

const isListModalVisible = ref(false);

const currentTab = computed<IRPTabType>(() => {
  const tabs = Object.keys(IRP_TAB_ROUTES) as IRPTabType[];
  return (
    tabs.find((tab) => IRP_TAB_ROUTES[tab] === route.name) ?? IRP_TAB.overview
  );
});

const activeTabName = computed(() =>
  irpTabName(currentTab.value, individualRecordStore.mdmType),
);

const currentRecordForList = computed(() => [
  { source_id: sourceId, master_id: sourcePrimaryKey },
]);

const partnerOrgIds = computed(() =>
  individualRecordStore.ownMasterSection.partners.items
    .map(({ partner_organization_uuid: uuid }) =>
      partnersStore.getPartnerOrgByUuid(uuid),
    )
    .filter((org) => !!org)
    .map((org) => org.id),
);

useHead({
  title: computed(() => `${activeTabName.value} - Record - Crossbeam`),
});

watch(
  () => [sourceId, sourcePrimaryKey] as const,
  async ([nextSourceId, nextSourcePrimaryKey]) => {
    individualRecordStore.setRecord(nextSourceId, nextSourcePrimaryKey);
    await Promise.all([
      populationsStore.readySync,
      partnersStore.readySync,
      ...fetchHeaderSections(),
      matchIssuesStore.fetchMatchIssuesBySourceIdAndRecordId({
        sourceId: nextSourceId,
        recordId: nextSourcePrimaryKey,
      }),
    ]);
  },
  { immediate: true },
);

function fetchHeaderSections() {
  if (currentTab.value === IRP_TAB.overview)
    individualRecordStore.fetchSection('all');
  return [
    individualRecordStore.fetchOwnSection('master'),
    individualRecordStore.fetchOwnSection('opportunities'),
  ];
}

function onTabChange(tab: IRPTabType) {
  router.push({ name: IRP_TAB_ROUTES[tab] });
}
</script>
